import { chartCategorical } from "@opus-dt/ui-tokens";
import { cn } from "@/lib/utils";
import { positionPct, zoomedDomain } from "./chart-scale";

export interface IndicatorRow {
  label: string;
  base: number | null;
  winner: number | null;
  second: number | null;
}

interface IndicatorCompareProps {
  rows: IndicatorRow[];
  winnerLabel: string;
  secondLabel: string;
}

// Séries do gráfico. Os índices NÃO são livres: a paleta tem vizinhos que passam no olho e falham
// no daltonismo (0 e 4 — roxo e azul — ficam a ΔE 13 mesmo com visão normal). 0 × 5 passa nos seis
// checos em claro e escuro. Trocar índice aqui exige revalidar, não é ajuste de gosto.
export const WINNER_COLOR = chartCategorical(0);
export const SECOND_COLOR = chartCategorical(5);

/**
 * Base × vencedor × 2º colocado por indicador, em faixa RECORTADA.
 *
 * Uma tabela de números resolve a consulta ("quanto deu?") e falha na leitura ("quem está à
 * frente, e por quanto?"). Um eixo de 0 a 100 falharia igual: 93,8 e 94,4 seriam a mesma barra. A
 * faixa recortada só é honesta com as pontas rotuladas — elas ficam visíveis, e a nota abaixo do
 * gráfico diz que o eixo não começa em zero.
 *
 * O trecho ROXO entre a base e o vencedor é o que se está comprando: sem ele, duas marcas soltas
 * num trilho obrigam a medir a distância com os olhos. Os raios são concêntricos porque empates
 * acontecem (Qualidade costuma empatar) — três marcas no mesmo ponto seguem legíveis.
 */
export function IndicatorCompare({ rows, winnerLabel, secondLabel }: IndicatorCompareProps) {
  return (
    <div>
      {rows.map((row) => {
        const domain = zoomedDomain(
          [row.base, row.winner, row.second].filter((v): v is number => v != null)
        );
        const delta = row.base != null && row.winner != null ? row.winner - row.base : null;
        return (
          <div
            key={row.label}
            className="grid grid-cols-[104px_30px_1fr_30px] items-center gap-x-2 border-b border-border-subtle py-2 last:border-0"
          >
            <span className="text-[12px]">{row.label}</span>
            <span className="text-right font-mono text-[9px] tabular-nums text-text-tertiary">
              {domain[0].toFixed(1)}
            </span>

            <span
              className="relative h-4"
              role="img"
              aria-label={describe(row, winnerLabel, secondLabel)}
            >
              <span className="absolute inset-x-0 top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-border-subtle" />
              <Span base={row.base} winner={row.winner} domain={domain} />
              <Marker value={row.second} domain={domain} size="h-2 w-2" color={SECOND_COLOR} />
              {/* base = referência: neutra e VAZADA de propósito — ponto de partida, não uma série
                  disputando atenção. A forma também a distingue sem depender de cor. */}
              <Marker
                value={row.base}
                domain={domain}
                size="h-3.5 w-3.5 border-2 border-text-secondary bg-bg-surface"
              />
              {/* O vencedor é a marca de MAIOR ênfase — desenhado por último e maior que o 2º.
                  Com o 2º maior, o olho pousava no candidato que não é a recomendação. */}
              <Marker value={row.winner} domain={domain} size="h-3 w-3" color={WINNER_COLOR} />
            </span>

            <span className="font-mono text-[9px] tabular-nums text-text-tertiary">
              {domain[1].toFixed(1)}
            </span>

            <span className="col-start-3 -mt-0.5 text-[10px] text-text-tertiary">
              {deltaText(delta)}
            </span>
            <span className="col-start-4 -mt-0.5 whitespace-nowrap text-right font-mono text-[11px] tabular-nums text-brand-700">
              {row.winner == null ? "—" : `${row.winner.toFixed(1)}%`}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function deltaText(delta: number | null): string {
  if (delta == null) return "";
  if (delta === 0) return "sem mudança vs base";
  return `${delta > 0 ? "+" : "−"}${Math.abs(delta).toFixed(2)} pp vs base`;
}

/** O gráfico é uma imagem: sem isto, um leitor de tela recebe três divs mudas. */
function describe(row: IndicatorRow, winnerLabel: string, secondLabel: string): string {
  const part = (v: number | null, who: string) => (v == null ? null : `${who} ${v.toFixed(2)}%`);
  return [
    row.label,
    part(row.base, "base"),
    part(row.winner, winnerLabel),
    part(row.second, secondLabel),
  ]
    .filter(Boolean)
    .join(", ");
}

/** O trecho percorrido da base até o vencedor — o ganho, desenhado. */
function Span({
  base,
  winner,
  domain,
}: {
  base: number | null;
  winner: number | null;
  domain: [number, number];
}) {
  if (base == null || winner == null || base === winner) return null;
  const a = positionPct(Math.min(base, winner), domain);
  const b = positionPct(Math.max(base, winner), domain);
  return (
    <span
      className="absolute left-[var(--sp-x)] top-1/2 h-1.5 w-[var(--sp-w)] -translate-y-1/2 rounded-full bg-brand-200"
      style={{ "--sp-x": `${a}%`, "--sp-w": `${b - a}%` } as React.CSSProperties}
      aria-hidden
    />
  );
}

function Marker({
  value,
  domain,
  size,
  color,
}: {
  value: number | null;
  domain: [number, number];
  size: string;
  color?: string;
}) {
  if (value == null) return null;
  return (
    <span
      className={cn(
        "absolute left-[var(--dot-x)] top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-bg-surface",
        size,
        color && "bg-[var(--dot-color)]"
      )}
      style={
        {
          "--dot-x": `${positionPct(value, domain)}%`,
          ...(color ? { "--dot-color": color } : {}),
        } as React.CSSProperties
      }
    />
  );
}
